import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { FaPlus, FaFileContract, FaBan, FaSync } from 'react-icons/fa';
import Swal from 'sweetalert2';
import '../../styles/pages/RRHH/ContratosPage.css';
import {
    DIAS_SEMANA, NOMBRE_DIA, JORNADAS, formatearHoras, horasDeLaJornada,
    maxHorasParcial, maxHorasSemanales, minutosTrabajadosDia, validarJornada
} from '../../utils/jornadaLaboral';
import { TIPOS_CONTRATO, advertenciasVigencia, validarVigencia } from '../../utils/vigenciaContrato';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

const DIAS_LABORALES = ['LUNES', 'MARTES', 'MIERCOLES', 'JUEVES', 'VIERNES'];

/** El formulario abre con turno fijo de lunes a viernes, de 09:00 a 18:00 y una hora de colación: 40 horas. */
const jornadaInicial = () => ({
    jornada: 'COMPLETA',
    tipo_turno: 'FIJO',
    horario: DIAS_SEMANA.reduce((horario, dia) => ({
        ...horario,
        [dia]: { trabaja: DIAS_LABORALES.includes(dia), entrada: '09:00', salida: '18:00', colacion: 60 }
    }), {}),
    horas_semanales: '',
    colacion_minutos: 30,
    descripcion_turnos: ''
});

/** Los días marcados, en el formato que guarda el contrato. */
const diasTrabajados = (horario) => DIAS_SEMANA
    .filter((dia) => horario[dia].trabaja)
    .map((dia) => ({
        dia,
        entrada: horario[dia].entrada,
        salida: horario[dia].salida,
        colacion: Number(horario[dia].colacion) || 0
    }));

/**
 * Página de Contratos Laborales
 * Gestión de contratos de trabajo
 */
const ContratosPage = ({ token, id_company }) => {
    const [contratos, setContratos] = useState([]);
    const [colaboradoresDisponibles, setColaboradoresDisponibles] = useState([]); // Sin finiquito pagado
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);

    // Filtros
    const [filtroEstado, setFiltroEstado] = useState('VIGENTE');

    // Formulario
    const [formData, setFormData] = useState({
        id_colaborador: '',
        tipo_contrato: 'INDEFINIDO',
        fecha_inicio: '',
        fecha_termino: '',
        sueldo_base: '',
        ...jornadaInicial(),
        cargo: '',
        lugar_trabajo: '',
        otras_clausulas: ''
    });

    // La jornada se valida con las mismas reglas que aplica el backend al guardar.
    const horarioPactado = diasTrabajados(formData.horario);
    const datosJornada = { ...formData, horario: horarioPactado };
    const erroresJornada = validarJornada(datosJornada);
    const horasJornada = horasDeLaJornada(datosJornada) || 0;
    const maximoLegal = maxHorasSemanales(formData.fecha_inicio);

    // La vigencia, igual. El tope de un año del plazo fijo sólo se advierte: el
    // sistema no sabe si el trabajador es gerente o tiene título (art. 159 N°4).
    const erroresVigencia = validarVigencia(formData);
    const advertenciasPlazo = advertenciasVigencia(formData);

    const setDia = (dia, campo, valor) => setFormData((prev) => ({
        ...prev,
        horario: { ...prev.horario, [dia]: { ...prev.horario[dia], [campo]: valor } }
    }));

    const fetchColaboradores = useCallback(async () => {
        try {
            // Cargar colaboradores disponibles (sin finiquito pagado)
            const responseDisponibles = await axios.get(`${API_BASE_URL}/api/rrhh/colaboradores`, {
                params: { id_company, excluir_finiquitados: 'true' },
                headers: { Authorization: `Bearer ${token}` }
            });

            if (responseDisponibles.data.success) {
                setColaboradoresDisponibles(responseDisponibles.data.data);
            }
        } catch (error) {
            console.error('Error al cargar colaboradores:', error);
        }
    }, [id_company, token]);

    const fetchContratos = useCallback(async () => {
        try {
            setLoading(true);
            const params = { id_company, estado: filtroEstado };

            const response = await axios.get(`${API_BASE_URL}/api/rrhh/contratos`, {
                params,
                headers: { Authorization: `Bearer ${token}` }
            });

            if (response.data.success) {
                setContratos(response.data.data);
            }
        } catch (error) {
            console.error('Error al cargar contratos:', error);
            Swal.fire('Error', 'No se pudieron cargar los contratos', 'error');
        } finally {
            setLoading(false);
        }
    }, [id_company, token, filtroEstado]);

    useEffect(() => {
        fetchColaboradores();
    }, [fetchColaboradores]);

    useEffect(() => {
        fetchContratos();
    }, [fetchContratos]);

    const handleReemplazarPdf = async (contrato) => {
        const input = document.createElement('input');
        input.type = 'file';
        input.accept = '.pdf';
        input.onchange = async (e) => {
            const file = e.target.files[0];
            if (!file) return;

            try {
                const formData = new FormData();
                formData.append('pdf', file);
                formData.append('id_company', id_company);
                formData.append('id_contrato', contrato.id);
                formData.append('id_colaborador', contrato.id_colaborador);

                await axios.post(
                    `${API_BASE_URL}/api/rrhh/contratos/upload-pdf`,
                    formData,
                    { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' } }
                );

                Swal.fire('PDF reemplazado', 'El PDF del contrato fue actualizado en R2', 'success');
                fetchContratos();
            } catch (error) {
                console.error('Error al reemplazar PDF:', error);
                Swal.fire('Error', error.response?.data?.error || 'No se pudo reemplazar el PDF', 'error');
            }
        };
        input.click();
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        const errores = erroresVigencia.concat(erroresJornada);
        if (errores.length > 0) {
            Swal.fire({ icon: 'warning', title: 'Revise el contrato', html: errores.join('<br>') });
            return;
        }

        try {
            const response = await axios.post(`${API_BASE_URL}/api/rrhh/contratos`, {
                ...formData,
                horario: horarioPactado,
                id_company
            }, {
                headers: { Authorization: `Bearer ${token}` }
            });

            if (response.data.success) {
                if (response.data.pdfUrl) {
                    Swal.fire({
                        title: 'Contrato Generado',
                        text: 'El contrato ha sido generado exitosamente',
                        icon: 'success',
                        showCancelButton: true,
                        confirmButtonText: 'Descargar PDF',
                        cancelButtonText: 'Cerrar'
                    }).then((result) => {
                        if (result.isConfirmed) {
                            window.open(response.data.pdfUrl.startsWith('http') ? response.data.pdfUrl : `${API_BASE_URL}${response.data.pdfUrl}`, '_blank');
                        }
                    });
                } else {
                    Swal.fire('Éxito', 'Contrato generado correctamente', 'success');
                }

                setShowModal(false);
                resetForm();
                fetchContratos();
            }
        } catch (error) {
            console.error('Error al guardar contrato:', error);
            Swal.fire('Error', error.response?.data?.message || 'Error al guardar', 'error');
        }
    };

    const handleTerminar = async (id) => {
        try {
            const { value: formValues } = await Swal.fire({
                title: 'Terminar Contrato',
                html:
                    '<input id="fecha_termino" type="date" class="swal2-input" placeholder="Fecha de término">' +
                    '<textarea id="motivo" class="swal2-input" placeholder="Motivo de término"></textarea>',
                focusConfirm: false,
                showCancelButton: true,
                confirmButtonText: 'Terminar',
                cancelButtonText: 'Cancelar',
                preConfirm: () => {
                    return {
                        fecha_termino_real: document.getElementById('fecha_termino').value,
                        motivo_termino: document.getElementById('motivo').value
                    };
                }
            });

            if (formValues && formValues.fecha_termino_real) {
                const response = await axios.patch(
                    `${API_BASE_URL}/api/rrhh/contratos/${id}/terminar`,
                    formValues,
                    { headers: { Authorization: `Bearer ${token}` } }
                );

                if (response.data.success) {
                    Swal.fire('Éxito', 'Contrato terminado correctamente', 'success');
                    fetchContratos();
                }
            }
        } catch (error) {
            console.error('Error al terminar contrato:', error);
            Swal.fire('Error', 'No se pudo terminar el contrato', 'error');
        }
    };

    const handleColaboradorChange = async (id_colaborador) => {
        setFormData({...formData, id_colaborador});

        if (id_colaborador) {
            try {
                const response = await axios.get(`${API_BASE_URL}/api/rrhh/colaboradores/${id_colaborador}`, {
                    headers: { Authorization: `Bearer ${token}` }
                });

                if (response.data.success) {
                    const colaborador = response.data.data;
                    const tipoContrato = TIPOS_CONTRATO.includes(colaborador.tipo_contrato) ? colaborador.tipo_contrato : 'INDEFINIDO';
                    setFormData(prev => ({
                        ...prev,
                        id_colaborador,
                        tipo_contrato: tipoContrato,
                        fecha_termino: tipoContrato === 'PLAZO_FIJO' ? prev.fecha_termino : '',
                        fecha_inicio: colaborador.fecha_ingreso ? colaborador.fecha_ingreso.split('T')[0] : '',
                        sueldo_base: colaborador.sueldo_base || '',
                        cargo: colaborador.cargo || '',
                        jornada: JORNADAS.includes(colaborador.jornada) ? colaborador.jornada : 'COMPLETA',
                        horas_semanales: colaborador.horas_semanales || '',
                        lugar_trabajo: colaborador.direccion || ''
                    }));
                }
            } catch (error) {
                console.error('Error al cargar datos del colaborador:', error);
            }
        }
    };

    const resetForm = () => {
        setFormData({
            id_colaborador: '',
            tipo_contrato: 'INDEFINIDO',
            fecha_inicio: '',
            fecha_termino: '',
            sueldo_base: '',
            ...jornadaInicial(),
            cargo: '',
            lugar_trabajo: '',
            otras_clausulas: ''
        });
    };

    return (
        <div className="contratos-container">
            <div className="contratos-header">
                <h1><FaFileContract /> Contratos Laborales</h1>
                <button className="btn-nuevo" onClick={() => { resetForm(); setShowModal(true); }}>
                    <FaPlus /> Nuevo Contrato
                </button>
            </div>

            {/* Filtros */}
            <div className="contratos-filters">
                <div className="filter-group">
                    <label>Estado</label>
                    <select value={filtroEstado} onChange={(e) => setFiltroEstado(e.target.value)}>
                        <option value="">Todos</option>
                        <option value="VIGENTE">Vigentes</option>
                        <option value="TERMINADO">Terminados</option>
                    </select>
                </div>
            </div>

            {/* Tabla de contratos */}
            {loading ? (
                <div className="loading">Cargando contratos...</div>
            ) : (
                <div className="contratos-table-container">
                    <table className="contratos-table">
                        <thead>
                            <tr>
                                <th>Colaborador</th>
                                <th>RUT</th>
                                <th>Fecha Inicio</th>
                                <th>Fecha Término</th>
                                <th>Sueldo Pactado</th>
                                <th>Estado</th>
                                <th>Acciones</th>
                            </tr>
                        </thead>
                        <tbody>
                            {contratos.length === 0 ? (
                                <tr>
                                    <td colSpan="7" style={{ textAlign: 'center', padding: '30px' }}>
                                        No se encontraron contratos
                                    </td>
                                </tr>
                            ) : (
                                contratos.map((contrato) => (
                                    <tr key={contrato.id}>
                                        <td>{contrato.nombres} {contrato.apellido_paterno}</td>
                                        <td>{contrato.rut}</td>
                                        <td>{new Date(contrato.fecha_inicio).toLocaleDateString('es-CL')}</td>
                                        <td>
                                            {contrato.fecha_termino
                                                ? new Date(contrato.fecha_termino).toLocaleDateString('es-CL')
                                                : 'Indefinido'
                                            }
                                        </td>
                                        <td>${parseInt(contrato.sueldo_base || 0).toLocaleString('es-CL')}</td>
                                        <td>
                                            <span className={`badge badge-${contrato.estado?.toLowerCase() || 'vigente'}`}>
                                                {contrato.estado || 'VIGENTE'}
                                            </span>
                                        </td>
                                        <td className="actions-cell">
                                            {contrato.url_contrato_firmado && (
                                                <button
                                                    className="btn-download"
                                                    onClick={() => window.open(contrato.url_contrato_firmado.startsWith('http') ? contrato.url_contrato_firmado : `${API_BASE_URL}/uploads/${contrato.url_contrato_firmado}`, '_blank')}
                                                    title="Ver PDF"
                                                >
                                                    <FaFileContract />
                                                </button>
                                            )}
                                            {contrato.url_contrato_firmado && (
                                                <button
                                                    className="btn-info"
                                                    onClick={() => handleReemplazarPdf(contrato)}
                                                    title="Reemplazar PDF"
                                                >
                                                    <FaSync />
                                                </button>
                                            )}
                                            {contrato.estado === 'VIGENTE' && (
                                                <button
                                                    className="btn-terminar"
                                                    onClick={() => handleTerminar(contrato.id)}
                                                    title="Terminar contrato"
                                                >
                                                    <FaBan />
                                                </button>
                                            )}
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Modal de formulario */}
            {showModal && (
                <div className="modal-overlay" onClick={() => { setShowModal(false); resetForm(); }}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                        <h2>Nuevo Contrato</h2>
                        <form onSubmit={handleSubmit}>
                            <div className="form-grid">
                                <div className="form-group">
                                    <label>Colaborador *</label>
                                    <select
                                        value={formData.id_colaborador}
                                        onChange={(e) => handleColaboradorChange(e.target.value)}
                                        required
                                    >
                                        <option value="">Seleccione...</option>
                                        {colaboradoresDisponibles.map(col => (
                                            <option key={col.id} value={col.id}>
                                                {col.nombres} {col.apellido_paterno}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div className="form-group">
                                    <label>Tipo de Contrato *</label>
                                    <select
                                        value={formData.tipo_contrato}
                                        onChange={(e) => setFormData({
                                            ...formData,
                                            tipo_contrato: e.target.value,
                                            // Un contrato indefinido no tiene fecha de término.
                                            fecha_termino: e.target.value === 'PLAZO_FIJO' ? formData.fecha_termino : ''
                                        })}
                                        required
                                    >
                                        <option value="INDEFINIDO">Indefinido</option>
                                        <option value="PLAZO_FIJO">Plazo Fijo</option>
                                    </select>
                                </div>
                                <div className="form-group">
                                    <label>Fecha Inicio *</label>
                                    <input
                                        type="date"
                                        value={formData.fecha_inicio}
                                        onChange={(e) => setFormData({...formData, fecha_inicio: e.target.value})}
                                        required
                                    />
                                </div>
                                {formData.tipo_contrato === 'PLAZO_FIJO' && (
                                    <div className="form-group">
                                        <label>Fecha Término *</label>
                                        <input
                                            type="date"
                                            value={formData.fecha_termino}
                                            min={formData.fecha_inicio || undefined}
                                            onChange={(e) => setFormData({...formData, fecha_termino: e.target.value})}
                                            required
                                        />
                                        {formData.fecha_termino && erroresVigencia.map((error) => (
                                            <span key={error} className="campo-error">{error}</span>
                                        ))}
                                        {advertenciasPlazo.map((aviso) => (
                                            <span key={aviso} className="campo-advertencia">{aviso}</span>
                                        ))}
                                    </div>
                                )}
                                <div className="form-group">
                                    <label>Sueldo Base *</label>
                                    <input
                                        type="number"
                                        value={formData.sueldo_base}
                                        onChange={(e) => setFormData({...formData, sueldo_base: e.target.value})}
                                        required
                                    />
                                </div>
                                <div className="form-group">
                                    <label>Jornada Laboral *</label>
                                    <select
                                        value={formData.jornada}
                                        onChange={(e) => setFormData({...formData, jornada: e.target.value})}
                                        required
                                    >
                                        <option value="COMPLETA">Completa</option>
                                        <option value="PARCIAL">Parcial (art. 40 bis)</option>
                                        <option value="EXCLUIDA">Excluida de limitación (art. 22)</option>
                                    </select>
                                </div>
                                {formData.jornada !== 'EXCLUIDA' && (
                                    <div className="form-group">
                                        <label>Tipo de Turno *</label>
                                        <select
                                            value={formData.tipo_turno}
                                            onChange={(e) => setFormData({...formData, tipo_turno: e.target.value})}
                                            required
                                        >
                                            <option value="FIJO">Fijo</option>
                                            <option value="ROTATIVO">Rotativo</option>
                                        </select>
                                    </div>
                                )}
                                <div className="form-group">
                                    <label>Cargo *</label>
                                    <input
                                        type="text"
                                        value={formData.cargo}
                                        onChange={(e) => setFormData({...formData, cargo: e.target.value})}
                                        required
                                    />
                                </div>
                                <div className="form-group">
                                    <label>Domicilio del Trabajador</label>
                                    <input
                                        type="text"
                                        value={formData.lugar_trabajo}
                                        onChange={(e) => setFormData({...formData, lugar_trabajo: e.target.value})}
                                    />
                                </div>
                                <div className="jornada-seccion">
                                    <div className="jornada-titulo">Horario de trabajo</div>

                                    {formData.jornada === 'EXCLUIDA' ? (
                                        <p className="jornada-nota">
                                            Sin horario ni límite de horas. Corresponde a gerentes, administradores,
                                            apoderados con facultades de administración y a quienes trabajan sin
                                            fiscalización superior inmediata.
                                        </p>
                                    ) : formData.tipo_turno === 'FIJO' ? (
                                        <table className="horario-tabla">
                                            <thead>
                                                <tr>
                                                    <th>Día</th>
                                                    <th>Entrada</th>
                                                    <th>Salida</th>
                                                    <th>Colación (min)</th>
                                                    <th>Horas</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {DIAS_SEMANA.map((dia) => {
                                                    const d = formData.horario[dia];
                                                    const minutos = minutosTrabajadosDia(d);
                                                    return (
                                                        <tr key={dia} className={d.trabaja ? '' : 'dia-libre'}>
                                                            <td>
                                                                <label className="horario-dia">
                                                                    <input
                                                                        type="checkbox"
                                                                        checked={d.trabaja}
                                                                        onChange={(e) => setDia(dia, 'trabaja', e.target.checked)}
                                                                    />
                                                                    {NOMBRE_DIA[dia]}
                                                                </label>
                                                            </td>
                                                            <td>
                                                                <input
                                                                    type="time"
                                                                    value={d.entrada}
                                                                    disabled={!d.trabaja}
                                                                    onChange={(e) => setDia(dia, 'entrada', e.target.value)}
                                                                />
                                                            </td>
                                                            <td>
                                                                <input
                                                                    type="time"
                                                                    value={d.salida}
                                                                    disabled={!d.trabaja}
                                                                    onChange={(e) => setDia(dia, 'salida', e.target.value)}
                                                                />
                                                            </td>
                                                            <td>
                                                                <input
                                                                    type="number"
                                                                    min="0"
                                                                    step="5"
                                                                    value={d.colacion}
                                                                    disabled={!d.trabaja}
                                                                    onChange={(e) => setDia(dia, 'colacion', e.target.value)}
                                                                />
                                                            </td>
                                                            <td className="horario-horas">
                                                                {d.trabaja && minutos > 0 ? formatearHoras(minutos / 60, true) : '—'}
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    ) : (
                                        <div className="jornada-rotativo">
                                            <div className="form-group jornada-rotativo-descripcion">
                                                <label>Sistema de turnos *</label>
                                                <textarea
                                                    rows="2"
                                                    placeholder="Ej.: turno mañana de 07:00 a 15:00 horas y turno tarde de 15:00 a 23:00 horas, con rotación semanal"
                                                    value={formData.descripcion_turnos}
                                                    onChange={(e) => setFormData({...formData, descripcion_turnos: e.target.value})}
                                                />
                                            </div>
                                            <div className="form-group">
                                                <label>Horas Semanales *</label>
                                                <input
                                                    type="number"
                                                    min="1"
                                                    step="0.5"
                                                    value={formData.horas_semanales}
                                                    onChange={(e) => setFormData({...formData, horas_semanales: e.target.value})}
                                                />
                                            </div>
                                            <div className="form-group">
                                                <label>Colación diaria (min)</label>
                                                <input
                                                    type="number"
                                                    min="0"
                                                    step="5"
                                                    value={formData.colacion_minutos}
                                                    onChange={(e) => setFormData({...formData, colacion_minutos: e.target.value})}
                                                />
                                            </div>
                                        </div>
                                    )}

                                    {formData.jornada !== 'EXCLUIDA' && (
                                        <div className="jornada-total">
                                            Total: <strong>{formatearHoras(horasJornada, true)} semanales</strong>
                                            {' · '}máximo legal a la fecha de inicio: {maximoLegal} h
                                            {formData.jornada === 'PARCIAL' && ` (parcial: hasta ${formatearHoras(maxHorasParcial(formData.fecha_inicio), true)})`}
                                        </div>
                                    )}

                                    {erroresJornada.length > 0 && (
                                        <ul className="jornada-errores">
                                            {erroresJornada.map((error) => <li key={error}>{error}</li>)}
                                        </ul>
                                    )}
                                </div>
                                <div className="form-group full-width">
                                    <label>Otras cláusulas (opcional)</label>
                                    <textarea
                                        value={formData.otras_clausulas}
                                        onChange={(e) => setFormData({...formData, otras_clausulas: e.target.value})}
                                        rows="3"
                                        placeholder="Se agregan al contrato como la cláusula &quot;Estipulaciones adicionales&quot;. Cada línea es un párrafo. Si queda vacío, no se agrega nada."
                                    />
                                </div>
                            </div>

                            <div className="modal-actions">
                                <button type="button" className="btn-cancel" onClick={() => { setShowModal(false); resetForm(); }}>
                                    Cancelar
                                </button>
                                <button type="submit" className="btn-save">
                                    Generar Contrato
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

        </div>
    );
};

export default ContratosPage;
